import { AlertTriangle, Braces, Loader2, Monitor, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Market } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { ToggleGroup, ToggleGroupItem } from '@nucleus-commerce/ui/components/toggle-group';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { BlockPreviewState } from '../../hooks/use-block-preview';
import type { BlockFormInput } from '../../schemas/block-form-schema';

import { BannerPreview, type PreviewDevice } from './banner-preview';
import { bannerSlides, countIssues, railView } from './preview-model';
import { PreviewPayloadSheet } from './preview-payload-sheet';
import { RailPreview } from './rail-preview';

export interface PreviewStoreOption {
  id: string;
  name: string;
}

export interface BlockPreviewPanelViewProps {
  values: BlockFormInput;
  preview: Pick<BlockPreviewState, 'block' | 'sent' | 'enabled' | 'isUpdating'> & {
    /** Any error the admin's error messages understand; null when the last request succeeded. */
    error: unknown;
  };
  device: PreviewDevice;
  onDeviceChange: (device: PreviewDevice) => void;
  /** The stores a shared block can be previewed in; null for a store block (no picker). */
  stores: readonly PreviewStoreOption[] | null;
  /** Store the preview resolves in. */
  storeId: string | null;
  onStoreChange: (storeId: string) => void;
  storeName: string;
  markets: readonly Market[];
  marketId: string | null;
  onMarketChange: (marketId: string) => void;
  /** Current banner slide. */
  slide: number;
  onSlideChange: (index: number) => void;
}

/**
 * The preview card without data fetching: toolbar (device, store, market, JSON), the canvas and
 * the illustrative note. BlockPreviewPanel feeds it from the form and the preview query.
 */
export function BlockPreviewPanelView({
  values,
  preview,
  device,
  onDeviceChange,
  stores,
  storeId,
  onStoreChange,
  storeName,
  markets,
  marketId,
  onMarketChange,
  slide,
  onSlideChange,
}: BlockPreviewPanelViewProps) {
  const { t } = useTranslation('content');
  const message = useErrorMessage();
  const [jsonOpen, setJsonOpen] = useState(false);
  const slides =
    values.kind === 'banner' ? bannerSlides(values, preview.sent, preview.block) : null;
  const rail = values.kind === 'rail' ? railView(values, preview.sent, preview.block) : null;
  const issues = slides ? countIssues(slides) : rail ? countIssues(rail) : 0;
  const name = values.name.trim() || t('blockPreview.untitled');
  const route =
    stores === null
      ? `POST /admin/v1/stores/${storeId ?? '{storeId}'}/blocks/preview`
      : `POST /admin/v1/org/blocks/preview?storeId=${storeId ?? ''}`;

  return (
    <Card id="block-preview" className="gap-4">
      <CardHeader>
        <CardTitle>{t('blockPreview.title')}</CardTitle>
        <CardAction className="flex items-center gap-2">
          {preview.isUpdating ? (
            <Loader2
              className="text-muted-foreground size-4 animate-spin"
              role="img"
              aria-label={t('blockPreview.updating')}
            />
          ) : null}
          {issues > 0 ? (
            <Badge
              variant="outline"
              className="border-amber-500/40 text-amber-700 dark:text-amber-400"
              title={t('blockPreview.issuesHint')}
            >
              <AlertTriangle aria-hidden />
              {t('blockPreview.issues', { count: issues })}
            </Badge>
          ) : null}
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={device}
            onValueChange={(value) => {
              if (value === 'desktop' || value === 'phone') onDeviceChange(value);
            }}
            aria-label={t('blockPreview.device')}
          >
            <ToggleGroupItem value="desktop" aria-label={t('blockPreview.devices.desktop')}>
              <Monitor aria-hidden />
            </ToggleGroupItem>
            <ToggleGroupItem value="phone" aria-label={t('blockPreview.devices.phone')}>
              <Smartphone aria-hidden />
            </ToggleGroupItem>
          </ToggleGroup>
          <PreviewPayloadSheet
            open={jsonOpen}
            onOpenChange={setJsonOpen}
            trigger={
              <Button type="button" variant="outline" size="sm">
                <Braces aria-hidden />
                {t('blockPreview.json')}
              </Button>
            }
            title={t('blockPreview.jsonTitle')}
            description={
              <>
                {t('blockPreview.jsonDescription', { store: storeName })} <code>{route}</code>
              </>
            }
            label={t('blockPreview.jsonLabel')}
            payload={preview.block}
            isPending={preview.isUpdating && !preview.block}
            error={preview.error ?? undefined}
            empty={t('blockPreview.jsonEmpty')}
          />
        </div>
        <div className={cn('grid gap-2', stores !== null && 'grid-cols-2')}>
          {stores !== null ? (
            <Select value={storeId ?? ''} onValueChange={onStoreChange}>
              <SelectTrigger size="sm" className="w-full" aria-label={t('blockPreview.store')}>
                <SelectValue placeholder={t('blockPreview.store')} />
              </SelectTrigger>
              <SelectContent>
                {stores.map((store) => (
                  <SelectItem key={store.id} value={store.id}>
                    {store.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          <Select
            value={marketId ?? ''}
            onValueChange={onMarketChange}
            disabled={markets.length === 0}
          >
            <SelectTrigger size="sm" className="w-full" aria-label={t('blockPreview.market')}>
              <SelectValue placeholder={t('blockPreview.market')} />
            </SelectTrigger>
            <SelectContent>
              {markets.map((market) => (
                <SelectItem key={market.id} value={market.id}>
                  {market.isDefault
                    ? t('blockPreview.marketDefault', { name: market.name })
                    : market.name}{' '}
                  · {market.currency}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div
          role="region"
          aria-live="polite"
          aria-label={t('blockPreview.canvasLabel', { name })}
          data-device={device}
          className={cn(
            'bg-muted/40 rounded-md p-3',
            device === 'phone' && 'mx-auto w-full max-w-[360px] rounded-[1.25rem] border-4 p-2',
          )}
        >
          {slides ? (
            <BannerPreview
              slides={slides}
              index={slide}
              onIndexChange={onSlideChange}
              device={device}
            />
          ) : rail ? (
            <RailPreview rail={rail} device={device} storeName={storeName} />
          ) : null}
        </div>
        {preview.error ? (
          <p className="text-destructive text-xs" role="status">
            {t('blockPreview.unavailable', { message: message(preview.error) })}
          </p>
        ) : null}
      </CardContent>
      <CardFooter>
        <p className="text-muted-foreground text-xs">{t('blockPreview.illustrative')}</p>
      </CardFooter>
    </Card>
  );
}
